import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { TranslateModule, TranslateService } from '@itonics/ngx-translate-simple';

import { TrackedEvents } from '@innovation-os-ng/ui/utils';
import { ColorPalettes, ThemeService } from '@innovation-os-ng/ui/viz-tools-ng';

import { ZoomTransformation } from '../../common/zoom/models/zoom-model';
import { GeoMap } from '../geojson/world-110m';

import {
  WorldMapCountry,
  WorldMapData,
  WorldMapFeature,
  WorldMapGrowthData,
  WorldMapShape,
  WorldMapTooltipAlign,
  WorldMapType
} from '../models/worldmap-model';

import { WorldmapComponentLegends } from './worldmap-legends';

import * as d3 from 'd3';
// D3 V7 does not support any more .nest()
// in order to use the old method we need to use d3-collection
import { CommonModule } from '@angular/common';
import { deepEqual } from '@innovation-os-ng/helpers';
import { LegendBoxModule } from '@innovation-os-ng/ui/legend-box';
import * as d3Collection from 'd3-collection';
import { CommonChartModule } from '../../common/common-chart.module';
import { BubbleComponent } from './bubble/bubble.component';
import { PieComponent } from './pie/pie.component';
import { ShapeComponent } from './shape/shape.component';
import { SymbolComponent } from './symbol/symbol.component';
import { WorldmapTooltipComponent } from './worldmap-tooltip/worldmap-tooltip.component';

@Component({
  selector: 'chart-worldmap',
  templateUrl: './worldmap.component.html',
  styleUrls: ['./worldmap.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CommonChartModule,
    LegendBoxModule,
    ShapeComponent,
    SymbolComponent,
    PieComponent,
    BubbleComponent,
    WorldmapTooltipComponent,
    TranslateModule
  ]
})
export class WorldmapUiComponent extends WorldmapComponentLegends implements OnInit, OnDestroy {
  /**
   *  Elements
   */
  private shapes: Array<WorldMapShape>;

  public noHighlightedShapes: Array<WorldMapShape>;
  public excludedShapes: Array<WorldMapShape>;
  public selectedShapes: Array<WorldMapShape>;

  /**
   * MAPS (dictionaries)
   */
  public worldFeaturesMap: {
    [key: string]: WorldMapFeature;
  };

  public dataCountriesMap: {
    [key: string]: WorldMapCountry;
  };

  public countriesGrowthMap: {
    [key: string]: number;
  };

  /**
   * Cartography!
   */

  private projection;

  /**
   * CANVAS
   */
  public transform: ZoomTransformation;
  public zoom;
  public size: {
    height: number;
    width: number;
  };

  /**
   * DATA RANGES
   */

  public dataRangeScaled: Array<number>;
  public growthDataRange: Array<number>;

  /**
   * COLORS
   */

  /**
   * SUBJECT
   */

  private resizeSub: Subject<void> = new Subject();

  /**
   * SUBSCRIPTIONS
   */
  private subs: Subscription = new Subscription();

  @ViewChild('rectZoom', { static: true })
  rectZoom: ElementRef;

  /**
   * STATES
   */

  public worldmapActiveType: string = WorldMapType.GROWTH;

  public worldMapTypes = [WorldMapType.GROWTH, WorldMapType.BUBBLE, WorldMapType.CHOROPLETH, WorldMapType.PIE];

  // Because we activate choropleth via input we use this public variable
  public activateChoropleth: boolean;

  /**
   * INTERACTION
   */
  public countryHovered: WorldMapCountry;

  /**
   * Tooltip
   */
  public tooltipAlignment: string;
  public tooltipWidth: number = 200;
  public tooltipPosition: string;

  /**
   * ngMODELS
   */

  //   NgModel on select country
  @Output()
  countrySelectModelChange: EventEmitter<Array<string>> = new EventEmitter();

  public countriesSelected: Array<string>;

  @Input()
  get countrySelectModel() {
    return this.countriesSelected;
  }

  set countrySelectModel(val) {
    if (deepEqual((this.countriesSelected || []).sort(), (val || []).sort())) {
      return;
    }

    this.countriesSelected = val; // io management

    // update the highlighted
    this.getHighlightedShapes();
  }

  /**
   * OUTPUTS
   */

  @Output()
  worldmapTypeChanged: EventEmitter<[WorldMapType, boolean]> = new EventEmitter<[WorldMapType, boolean]>();

  // Track events via parent/ancestor
  @Output()
  public trackEvent: EventEmitter<string> = new EventEmitter();

  /**
   * INPUTS
   */
  @Input()
  categoryKey: string;

  /**
   * SET INPUTS
   */

  private _scale: (value: number) => number;

  @Input()
  set scale(scale: (value: number) => number) {
    this._scale = scale;

    if (this._data) {
      if (this._scale) {
        this._data.forEach((element) => {
          element.scaledValue = this._scale(element.value);
        });
      } else {
        //  we want to clean the scaledValues
        this._data.forEach((element) => {
          element.scaledValue = null;
        });
      }

      this.countries = this.getValues(this._data);
      this.dataCountriesMap = this.getValuesMap(this.countries);
      this.dataRange = this.getDataRange(this.countries);
      this.dataRangeScaled = this.getDataRangeScaled(this.countries);

      // Legends
      if (this.legendTranslation) {
        this.getLegendPie();
        this.getLegendChoropleth();
        this.getLegendBubble();

        this.getActiveLegends();
      }
    }
  }

  @Input()
  set data(data: Array<WorldMapData>) {
    this._data = data;

    //  If there is a scale available to apply to the visuals, use it

    if (this._scale) {
      this._data.forEach((element) => {
        element.scaledValue = this._scale(element.value);
      });
    }

    this.countries = this.getValues(data);
    this.dataCountriesMap = this.getValuesMap(this.countries);
    this.dataRange = this.getDataRange(this.countries);
    this.dataRangeScaled = this.getDataRangeScaled(this.countries);

    this.categoryMap = this.getCategoriesMap(data);
    this.categoryList = this.getCategoriesList(this.categoryMap);

    this.getColors();
    this.getCircleSizes();

    // Legends
    if (this.legendTranslation) {
      this.getLegendPie();
      this.getLegendChoropleth();
      this.getLegendBubble();

      this.getActiveLegends();
    }

    // update the highlighted
    this.getHighlightedShapes();
  }

  @Input()
  set growthData(data: Array<WorldMapGrowthData>) {
    this._growthData = data;
    if (this.legendTranslation) {
      this.getLegendGrowth();
      this.getActiveLegends();
    }
    if (data) {
      this.getGrowthData(data);
    }
  }

  // Exclusion
  public countriesExcluded: Array<string>;
  @Input()
  set excluded(data: Array<string>) {
    this.countriesExcluded = data;

    // update the highlighted
    this.getHighlightedShapes();
  }

  // Enable scroll zoom
  @Input()
  public enableZoomScroll: boolean;

  constructor(
    private readonly host: ElementRef,
    readonly themeService: ThemeService,
    readonly translate: TranslateService
  ) {
    super(translate, themeService);
  }

  ngOnInit() {
    if (!this._growthData) {
      // if there is not growthData we filter it out from worldMapTypes
      this.worldMapTypes = this.worldMapTypes.filter((t) => t !== WorldMapType.GROWTH);
    }

    if (!this.legendTranslation) {
      const promises = [
        this.translate.get(`chart-ng-worldmap.${WorldMapType.PIE}`).toPromise(),
        this.translate.get(`chart-ng-worldmap.${WorldMapType.BUBBLE}`).toPromise(),
        this.translate.get(`chart-ng-worldmap.${WorldMapType.CHOROPLETH}`).toPromise(),
        this.translate.get(`chart-ng-worldmap.${WorldMapType.GROWTH}`).toPromise()
      ];

      this.legendTranslation = {};
      Promise.all(promises).then(([pie, bubble, choropleth, growth]) => {
        this.legendTranslation[WorldMapType.PIE] = pie;
        this.legendTranslation[WorldMapType.BUBBLE] = bubble;
        this.legendTranslation[WorldMapType.CHOROPLETH] = choropleth;
        this.legendTranslation[WorldMapType.GROWTH] = growth;

        this.getLegendPie();
        this.getLegendChoropleth();
        this.getLegendBubble();
        this.getLegendGrowth();

        //  We show by default pie
        this.worldmapTypesStatus[WorldMapType.PIE] = true;

        this.getActiveLegends();

        // set default worldmap type
        this.legendsActive = [this.legendMap[WorldMapType.PIE]];
      });
    }

    //  Get the size for canvas
    this.size = {
      width: this.host.nativeElement.offsetWidth,
      height: this.host.nativeElement.offsetHeight
    };

    //  Get d3 projection
    this.projection = this.getGeoProjection();

    //  Get shapes to draw in the map
    this.shapes = this.getShapesPath();

    // update the highlighted
    this.getHighlightedShapes();

    //  Get shapes map to be able to map countries to shapes
    this.worldFeaturesMap = this.getShapesMap();

    //  We manage resize as subscription to be able to debounce it
    this.subs.add(
      this.resizeSub.pipe(debounceTime(100)).subscribe(() => {
        this.size = {
          width: this.host.nativeElement.offsetWidth,
          height: this.host.nativeElement.offsetHeight
        };
      })
    );

    this.worldMapTypes.forEach((type) => {
      //  initialize a map to store the states of the worldmap types selected
      this.worldmapTypesStatus[type] = false;
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  // Event on window resize
  @HostListener('window:resize')
  onResize() {
    this.resizeSub.next(null);
  }

  /*
    Data
  */

  public getHighlightedShapes() {
    if (!this.shapes) {
      return;
    }

    //  We update the selection and exclusion
    this.shapes.forEach((shape) => {
      shape.excluded = (this.countriesExcluded || []).indexOf(shape.id) !== -1;
      shape.selected = (this.countriesSelected || []).indexOf(shape.id) !== -1;

      // Update this info in the dataCountriesMap

      if (this.dataCountriesMap[shape.id]) {
        this.dataCountriesMap[shape.id] = {
          ...this.dataCountriesMap[shape.id],
          excluded: shape.excluded,
          selected: shape.selected
        };
      }
    });

    //  we update the arrays to be able to render no-highlighted/selected/excluded shapes
    this.noHighlightedShapes = this.shapes.filter((shape) => !shape.excluded && !shape.selected);
    this.excludedShapes = this.shapes.filter((shape) => shape.excluded);
    this.selectedShapes = this.shapes.filter((shape) => shape.selected);
  }

  public getValues(data: Array<WorldMapData>): Array<WorldMapCountry> {
    if (!data) {
      return [];
    }

    return d3Collection
      .nest()
      .key((d) =>
        // First we group by countries the data
        d.country.toUpperCase()
      )
      .rollup((categoryGroup: Array<WorldMapData>) => {
        // Second for each country we get the total sum
        const sumValues = d3.sum(categoryGroup, (d: WorldMapData) => d.value);

        // We build the country object
        const countries: {
          count: number;
          id: string;
          data: Array<WorldMapData>;
          scaledCount?: number;
        } = {
          count: sumValues,
          id: categoryGroup[0].country.toUpperCase(),
          data: categoryGroup
        };

        //  If a scale was provided e.g. Logarithmic, we want also to scale the total
        if (this._scale) {
          const sumValuesScaled = d3.sum(categoryGroup, (d: WorldMapData) => d.scaledValue);
          countries.scaledCount = sumValuesScaled;
        }

        return countries;
      })
      .entries(data)
      .map((d) => d.value);
  }

  private getValuesMap(data: Array<WorldMapCountry>): {
    [key: string]: WorldMapCountry;
  } {
    const map = {};

    data.forEach((item) => {
      map[item.id] = item;
    });

    return map;
  }

  private getGrowthData(data) {
    //  We want to map growth data to degrees from 180 to 360
    const absMax = Math.max(...data.map((a) => Math.abs(a.growth)));
    const maxDeg = 90;

    // Highest 180degrees -  lowest 360degrees
    const angles = data.map((d) => 270 - (d.growth / absMax) * maxDeg);

    //  Get the range
    this.growthDataRange = this.getGrowthDataRange(angles);

    this.countriesGrowthMap = {};
    data.forEach((d, i) => {
      this.countriesGrowthMap[d.country] = angles[i];
    });
  }

  /*
    World Map
  */
  private getGeoProjection() {
    return d3.geoPath().projection(
      d3
        .geoMercator()
        .scale(100)
        .translate([this.size.width / 2, this.size.height / 2])
    );
  }

  private getShapesPath() {
    return GeoMap.features.map((d: any) => ({
      path: this.projection(d),
      id: d.id
    }));
  }

  private getShapesMap() {
    const obj = {};
    GeoMap.features.forEach((feature: any) => {
      const centroid = this.projection.centroid(feature);
      const offset = feature.centroidOffset;

      obj[feature.id] = {
        ...feature,
        centroid: offset ? centroid.map((d, i) => d + offset[i]) : centroid
      };
    });
    return obj;
  }

  public changeWorldmapType(type: WorldMapType) {
    this.worldmapTypesStatus[type] = !this.worldmapTypesStatus[type];
    this.getActiveLegends();

    if (type === WorldMapType.CHOROPLETH) {
      this.activateChoropleth = !this.activateChoropleth;
    }
    this.worldmapTypeChanged.emit([type, this.worldmapTypesStatus[type]]);
  }

  /**
   * COLORS
   */

  private getColors() {
    this.twoStepColors = this.themeService.getDefaultPalette(ColorPalettes.Palette_Steps2);
    this.mainColor = this.themeService.getDefaultPalette(ColorPalettes.Palette_SingleB);
    this.sequentialColors = this.themeService.getDefaultPalette(ColorPalettes.Palette_SequentialMono);
    this.complementaryColors = this.themeService.getDefaultPalette(ColorPalettes.Palette_ComplementaryA);

    this.getCategoricalColors();
  }

  private getCategoricalColors() {
    let colorCategoryMap;

    if (this.categoryKey) {
      colorCategoryMap = this.themeService.registerCategorical(
        this.categoryKey,
        this.categoryList.map((c) => c.label)
      );

      // Assign colors to each category
      this.categoryList.forEach((category) => {
        category.color = colorCategoryMap[category.label];
      });
    } else {
      const colors = this.themeService.getDefaultPalette(ColorPalettes.Palette_CategoricalChart);

      // Assign colors to each category
      this.categoryList.forEach((category, i) => {
        category.color = colors[i % colors.length];
      });
    }
  }

  /*
    User Interaction
  */

  public selected(country: WorldMapShape) {
    //  We block interaction if the country is excluded
    if (country.excluded) {
      return;
    }

    if (this.countriesSelected && this.countriesSelected.find((c) => c === country.id)) {
      // if the country is already selected, we deselect
      this.countrySelectModel = this.countrySelectModel.filter((c) => c !== country.id);
      this.trackEvent.emit(TrackedEvents.VALUE_DESELECTED);
    } else {
      // if the country is not selected, we select
      this.countrySelectModel = (this.countrySelectModel || []).concat(country.id);
      this.trackEvent.emit(TrackedEvents.VALUE_SELECTED);
    }

    //  And we update model
    this.countrySelectModelChange.emit(this.countrySelectModel);
  }

  public zoomed(event) {
    this.transform = event;
  }

  public onHoverCountry(countryId: string, event: MouseEvent) {
    // In data CountriesMap we have ONLY the map of the countries with data
    // if the country hovered is not in the map we want the shape object that includes
    // exclusion/selection states and the id as well
    this.countryHovered = this.dataCountriesMap[countryId]
      ? this.dataCountriesMap[countryId]
      : this.shapes.find((shape) => shape.id === countryId);

    // If the position of the data points is close to the end move to the left
    const width = this.host.nativeElement.offsetWidth;
    this.tooltipAlignment = width - event.pageX < this.tooltipWidth ? WorldMapTooltipAlign.LEFT : WorldMapTooltipAlign.RIGHT;

    const marginX = this.tooltipAlignment === WorldMapTooltipAlign.LEFT ? -10 : 10;
    const marginY = 5;

    const doc = document.documentElement;
    const left = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
    const top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

    this.tooltipPosition = `translateZ(0) translate(${event.pageX - left + marginX * 2}px, ${event.pageY - top + marginY / 2}px)`;
  }

  public onLeaveCountry() {
    this.countryHovered = null;
  }

  public trackByCountry(index: number, country: WorldMapCountry) {
    return country.id;
  }
}
